import { useState, useCallback } from "react";
import { apiClient } from "../../../../api/httpClient";
import { toast } from "react-toastify";
import { updateBondNumber } from "../../../../globals/utils/commons";
import { increment } from "../../../../store/slices/countSlice";
import { extractErrorMessage } from "../../../../globals/utils/error-handler";
import { bondRetrievalPageConfig } from "../bondRetrievalPageConfig";
import {
  applyFcraOverrideToPayload,
  guardianshipGuardiansForApprovePayload,
  mergeGuardianshipGuardiansFromPatchResponse,
} from "../bondRetrievalApiMappers";

function approveBondUpdateLabel(globalState, context) {
  if (context === "va_billing_principal") {
    return `${globalState.billingInformation.billing_principal_name} (VA Bond)`;
  }
  return globalState.attorneyInformation?.lawFirm;
}

/**
 * Shared approve / reject / underwriting / approve-menu wiring for retrieve hooks.
 *
 * @param {object} params
 * @param {'va'|'trustee'|'guardianship'|'probate'} params.kind
 * @param {string} params.bond_id
 * @param {object|null} params.data retrieve response (for bond number + status)
 * @param {object} params.globalState
 * @param {string} params.billingMethod
 * @param {import('redux').Dispatch} params.dispatch
 * @param {(status: string)=>object} params.generatePayloadForStatus build PATCH/POST bodies
 * @param {React.Dispatch<React.SetStateAction<boolean>>} params.setRejectDialogOpen
 * @param {React.Dispatch<React.SetStateAction<null>>} params.setRejectFcraFile
 * @param {React.Dispatch<React.SetStateAction<boolean>>} params.setIsApproveLoading
 * @param {React.Dispatch<React.SetStateAction<boolean>>} params.setIsRejectLoading
 * @param {React.Dispatch<React.SetStateAction<object>>} [params.setGlobalState]
 */
export function useBondRetrievalWorkflow({
  kind,
  bond_id,
  data,
  globalState,
  billingMethod,
  dispatch,
  generatePayloadForStatus,
  setRejectDialogOpen,
  setRejectFcraFile,
  setIsApproveLoading,
  setIsRejectLoading,
  setGlobalState,
}) {
  const cfg = bondRetrievalPageConfig[kind];
  if (!cfg) {
    throw new Error(`useBondRetrievalWorkflow: unknown kind ${kind}`);
  }

  const [anchorEl, setAnchorEl] = useState(null);
  const [isUnderwritingLoading, setIsUnderwritingLoading] = useState(false);

  const handleApprove = useCallback(async (options) => {
    setIsApproveLoading(true);
    const payload = generatePayloadForStatus(cfg.approveGenerateStatus);
    const payloadWithoutBondNumber = { ...payload };
    delete payloadWithoutBondNumber.bondNumber;

    payload.ams_flow = options.ams_flow;
    payload.billing_method = options.billing_method;

    const currentBondNumber = Number(data.bond_number?.bondno_id || null);
    const newBondNumber = Number(globalState.bondNumber?.bond_number?.bondno_id || null);

    try {
      const response_update = await apiClient(cfg.updatePath(bond_id), "PATCH", payloadWithoutBondNumber);
      if (kind === "guardianship" && response_update?.guardians) {
        const guardians = guardianshipGuardiansForApprovePayload(
          response_update,
          globalState.basicInformation
        );
        if (guardians) {
          payloadWithoutBondNumber.guardians = guardians;
          payload.guardians = guardians;
        }
        setGlobalState?.((prev) =>
          mergeGuardianshipGuardiansFromPatchResponse(prev, response_update)
        );
      }
      if (
        response_update.bond.bond_number === null ||
        currentBondNumber !== newBondNumber
      ) {
        await updateBondNumber(
          Number(globalState.bondNumber.bond_number.bondno_id),
          Number(bond_id),
          approveBondUpdateLabel(globalState, cfg.bondNumberApproveContext)
        );
      }
      await apiClient(cfg.approvePath(bond_id), "POST", payload);
      setIsApproveLoading(false);
      toast.success("Bond Approved Successfully");
      dispatch(increment());
      setTimeout(() => {
        window.close();
      }, 2000);
    } catch (error) {
      setIsApproveLoading(false);
      const message = extractErrorMessage(error);
      toast.error(`Bond approval failed. ${message}`);
      console.error("Error updating the bond request:", error);
    }
  }, [
    bond_id,
    cfg,
    data,
    dispatch,
    generatePayloadForStatus,
    globalState,
    kind,
    setGlobalState,
    setIsApproveLoading,
  ]);

  const handleReject = useCallback(
    async (fcraOverride) => {
      setIsRejectLoading(true);
      const payload = generatePayloadForStatus("Rejected");
      applyFcraOverrideToPayload(payload, cfg.nestedKey, fcraOverride);

      try {
        await apiClient(cfg.updatePath(bond_id), "PATCH", payload);
        setIsRejectLoading(false);
        setRejectDialogOpen(false);
        setRejectFcraFile(null);
        toast.success("Bond Rejected Successfully");
        dispatch(increment());
        setTimeout(() => {
          window.close();
        }, 2000);
      } catch (error) {
        setIsRejectLoading(false);
        console.error("Error updating the bond request:", error);
      }
    },
    [bond_id, cfg, dispatch, generatePayloadForStatus, setIsRejectLoading, setRejectDialogOpen, setRejectFcraFile]
  );

  const handleUpdateUnderwriting = useCallback(
    async (status) => {
      try {
        if (status === "approved") {
          setIsApproveLoading(true);
        }
        else if (status === "rejected") {
          setIsRejectLoading(true);
        }
        setIsUnderwritingLoading(true);

        await apiClient(`/update_underwriting_status/${bond_id}/`, "PATCH", { status });

        toast.success(`Underwriting ${status} successfully!`);
      } catch {
        toast.error(`Failed to ${status} underwriting. Please try again.`);
      } finally {
        setIsApproveLoading(false);
        setIsRejectLoading(false);
        setIsUnderwritingLoading(false);
      }
    },
    [bond_id, setIsApproveLoading, setIsRejectLoading]
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleButtonClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleOptionSelect = useCallback(
    (options) => {
      if (options.ams_flow) {
        if (!billingMethod) {
          toast.error("Please select a Billing Method before Approval!  ");
          handleMenuClose();
          return;
        }
      }

      void handleApprove({
        ams_flow: options.ams_flow,
        billing_method: billingMethod || "",
      });
      handleMenuClose();
    },
    [billingMethod, handleApprove, handleMenuClose]
  );

  return {
    anchorEl,
    setAnchorEl,
    handleApprove,
    handleReject,
    handleUpdateUnderwriting,
    isUnderwritingLoading,
    setIsUnderwritingLoading,
    handleButtonClick,
    handleMenuClose,
    handleOptionSelect,
  };
}
